import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, MapPin, Check, MessageCircle } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { getWhatsAppLink, WHATSAPP_NUMBER } from '../lib/utils';

const features = [
  "Itinéraire personnalisé selon vos préférences",
  "Suggestions de sites touristiques",
  "Conseils sur les transports",
  "Recommandations de restaurants",
  "Carte détaillée de la destination"
];

export const TouristPlanPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    full_name: '', destination: '', arrival_date: '', return_date: '',
    phone: '', email: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const msg = [
      `*Demande de Plan Touristique*`,
      ``,
      `Nom: ${formData.full_name}`,
      `Tél: ${formData.phone}`,
      `Email: ${formData.email}`,
      `Destination: ${formData.destination}`,
      `Date arrivée: ${formData.arrival_date}`,
      `Date retour: ${formData.return_date}`
    ].join('\n');

    window.open(getWhatsAppLink(WHATSAPP_NUMBER, msg), '_blank');
    setFormData({ full_name: '', destination: '', arrival_date: '', return_date: '', phone: '', email: '' });
  };

  return (
    <div className="bg-[#020617] min-h-screen">
      {/* Header */}
      <section className="pt-6 pb-4 relative">
        <div className="absolute inset-0 gradient-radial opacity-30" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-[#94A3B8] hover:text-[#D4AF37] transition-colors mb-4" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" /> Retour à l'accueil
          </button>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center">
              <MapPin className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <div>
              <h1 className="text-xl md:text-3xl font-bold text-white">Plan Touristique</h1>
              <p className="text-[#94A3B8] text-xs">Itinéraires personnalisés pour vos voyages</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-4">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-xl mx-auto flex flex-col gap-6">
            {/* Info */}
            <div className="space-y-3">
              <motion.div className="card-luxury p-4" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-semibold text-white">Plan Personnalisé</h3>
                  <span className="text-2xl font-bold text-[#D4AF37]">30$</span>
                </div>
                <p className="text-[#94A3B8] text-xs mb-3">
                  Notre équipe d'experts prépare un plan de voyage complet basé sur votre destination et vos préférences.
                </p>
                <ul className="space-y-2">
                  {features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="w-3 h-3 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-[#94A3B8] text-xs">{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>

              <motion.div className="card-luxury p-3 bg-gradient-to-br from-amber-500/10 to-transparent border-amber-500/20" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}>
                <h4 className="font-semibold text-amber-400 mb-1 text-xs">Délai de Livraison</h4>
                <p className="text-[#94A3B8] text-xs">Votre plan sera livré dans 24 à 48 heures après confirmation.</p>
              </motion.div>
            </div>

            {/* Form */}
            <motion.div className="card-luxury p-4" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
              <h3 className="text-xs font-semibold text-white mb-3">Commander votre Plan</h3>
              <form onSubmit={handleSubmit} className="space-y-2.5" data-testid="tourist-plan-form">
                <div>
                  <Label className="text-[#94A3B8] text-xs">Nom Complet *</Label>
                  <Input value={formData.full_name} onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                    required placeholder="Votre nom" className="bg-white/5 border-white/10 text-white text-xs h-8" data-testid="input-name" />
                </div>
                <div>
                  <Label className="text-[#94A3B8] text-xs">Téléphone *</Label>
                  <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    required placeholder="+243..." className="bg-white/5 border-white/10 text-white text-xs h-8" data-testid="input-phone" />
                </div>
                <div>
                  <Label className="text-[#94A3B8] text-xs">Email *</Label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required placeholder="votre@email.com" className="bg-white/5 border-white/10 text-white text-xs h-8" data-testid="input-email" />
                </div>
                <div>
                  <Label className="text-[#94A3B8] text-xs">Destination *</Label>
                  <Input value={formData.destination} onChange={(e) => setFormData({...formData, destination: e.target.value})}
                    required placeholder="Ex: Paris, Dubai..." className="bg-white/5 border-white/10 text-white text-xs h-8" data-testid="input-destination" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[#94A3B8] text-xs">Date Arrivée *</Label>
                    <Input type="date" value={formData.arrival_date} onChange={(e) => setFormData({...formData, arrival_date: e.target.value})}
                      required className="bg-white/5 border-white/10 text-white text-xs h-8" data-testid="input-arrival" />
                  </div>
                  <div>
                    <Label className="text-[#94A3B8] text-xs">Date Retour *</Label>
                    <Input type="date" value={formData.return_date} onChange={(e) => setFormData({...formData, return_date: e.target.value})}
                      required className="bg-white/5 border-white/10 text-white text-xs h-8" data-testid="input-return" />
                  </div>
                </div>
                <button type="submit" className="w-full btn-gold py-2.5 text-xs flex items-center justify-center gap-2" data-testid="submit-btn">
                  <MessageCircle className="w-3.5 h-3.5" />
                  Envoyer via WhatsApp
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TouristPlanPage;
